/**
 * Turning a failure into something a person can act on.
 *
 * Errors used to be printed wherever they were caught: a wall of red carrying
 * an exception class, a stack of causes and a remedy, all at the same weight.
 * That reads as "something broke, good luck". The rule here is one line that
 * says what happened, one line that says what to do, and the raw text kept
 * behind a disclosure for whoever actually needs it.
 */
import { ApiError, isOfflineError } from '@/lib/apiClient'

export interface PresentedError {
  /** Short, human. Goes in the heading. */
  title: string
  /** One sentence. What happened, in the reader's terms. */
  message: string
  /** The raw text, shown only on request. */
  detail?: string
  /** Whether retrying the same request could plausibly work. */
  retryable: boolean
}

/**
 * Signatures the engine and the portal produce, mapped to plain language.
 *
 * Matched on the message rather than a code because several of these come back
 * from the portal itself, which has no codes to offer.
 */
const KNOWN: Array<{ test: RegExp; title: string; message: string; retryable: boolean }> = [
  {
    test: /maintenance|JSPG0036E|JSP Processing Error/i,
    title: 'The court portal is unavailable',
    message:
      'NJ Courts returned a maintenance page. This is on their side; the search itself was fine.',
    retryable: true,
  },
  {
    test: /unusual activity|throttl|too many requests|rate limit/i,
    title: 'The portal is throttling us',
    message:
      'Too many searches in a short window. Wait a few minutes before starting another.',
    retryable: true,
  },
  {
    // A geo-block: the portal refused the address outright, with a 403.
    test: /geo-?fenc|access denied|error 16|non-us/i,
    title: 'The portal blocked this location',
    message:
      'NJ Courts only serves US addresses. Connect to a US VPN exit and confirm the search page loads in an ordinary browser.',
    retryable: true,
  },
  {
    // Not a geo-block. The address was accepted and the session was scored as
    // automated, which is fixed by the opposite of the advice above — fewer
    // hops and a better-reputation address, not another proxy.
    test: /bot challenge|scored as automated|refused the browser session|unusual activity/i,
    title: 'The portal scored the session as automated',
    message:
      'The address reached the portal but was not trusted. Datacenter and proxy addresses score worst — try without a proxy, or with a residential one, and leave a few minutes between searches.',
    retryable: true,
  },
  {
    test: /blocked|incapsula|imperva/i,
    title: 'The portal refused the connection',
    message: 'The site rejected the request before it reached the search.',
    retryable: true,
  },
  {
    // reCAPTCHA refused to mint a token, which is a scoring decision about the
    // address — not a request for someone to solve a puzzle. The old wording
    // ("start it from the Live search tab") sent people back to the tab they
    // had just used.
    test: /recaptcha declined|never produced its recaptcha|captcha token/i,
    title: 'The portal would not trust this session',
    message:
      'reCAPTCHA refused to issue a token for this address. Datacenter and VPN addresses score worst; a residential US proxy scores best. Leave a few minutes between searches.',
    retryable: true,
  },
  {
    test: /captcha|recaptcha/i,
    title: 'The portal asked for a captcha',
    message: 'The search needs a browser session. Start it from the Live search tab.',
    retryable: false,
  },
  {
    test: /timed out|timeout|ETIMEDOUT/i,
    title: 'The request timed out',
    message: 'Nothing came back in time. The portal may be slow rather than down.',
    retryable: true,
  },
]

function firstSentence(text: string): string {
  const trimmed = text.trim()
  const cut = trimmed.search(/(?<=[.!?])\s|\n/)
  if (cut > 0 && cut < 180) return trimmed.slice(0, cut + 1).trim()
  return trimmed.length > 180 ? `${trimmed.slice(0, 177).trimEnd()}…` : trimmed
}

export function presentError(error: unknown, fallbackTitle = 'Something went wrong'): PresentedError {
  if (isOfflineError(error)) {
    return {
      title: 'Cannot reach the API',
      message: 'The request never left the browser. Is the backend running on port 8000?',
      detail: error instanceof Error ? error.message : undefined,
      retryable: true,
    }
  }

  const raw =
    error instanceof ApiError
      ? [error.message, error.detail].filter(Boolean).join(': ')
      : error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : ''

  const known = KNOWN.find((entry) => entry.test.test(raw))
  if (known) {
    return { title: known.title, message: known.message, detail: raw, retryable: known.retryable }
  }

  if (error instanceof ApiError) {
    const server = error.status >= 500
    return {
      title: server ? 'The server failed to answer' : fallbackTitle,
      message: firstSentence(error.message) || 'The request was rejected.',
      detail: error.detail ? `${error.code}: ${error.detail}` : error.code,
      retryable: server,
    }
  }

  return {
    title: fallbackTitle,
    message: raw ? firstSentence(raw) : 'No further detail was reported.',
    detail: raw && raw !== firstSentence(raw) ? raw : undefined,
    retryable: true,
  }
}

/**
 * The same treatment for a job's stored error string, which arrives as one
 * long line assembled by the engine.
 */
export function presentJobError(text: string): PresentedError {
  return presentError(text, 'The job failed')
}

/**
 * A capture verdict, split into what happened and what to do about it.
 *
 * The engine assembles these as
 * `INCOMPLETE — the capture failed: <problem> → <remedy>`, and the captures
 * list renders one per row. Printed whole, fifteen of them are a wall of
 * repeated prefix; the badge beside the row already says INCOMPLETE, and
 * "the capture failed" is the same fact a third time.
 */
export function splitVerdict(text: string): { problem: string; remedy?: string } {
  const cleaned = text
    .trim()
    .replace(/^[A-Z][A-Z_ ]*\s*[—–-]\s*/, '')
    .replace(/^the capture (?:failed|stored nothing):\s*/i, '')
    // "PARTIAL — the run stopped early after storing N record(s)…" leads with
    // the reassurance, which the badge and the record count already give. The
    // reason it stopped is the part worth reading, so promote that.
    .replace(/^the run stopped early after storing .*?It stopped because:\s*/is, '')

  const [problem, ...rest] = cleaned.split(/\s*(?:→|->)\s*/)
  const remedy = rest.join(' ').trim()
  return {
    problem: problem.trim() || cleaned,
    remedy: remedy || undefined,
  }
}
